import {
  Flex,
  Heading,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  Tab,
  TabList,
  Tabs,
  TabPanels,
  Text,
  Badge,
} from "@chakra-ui/react";
import { useQuery } from "@tanstack/react-query";
import { PatientTaskInstanceId } from "../../shared/schema/schema";
import { Messages, QueryParamsOf } from "../../core/api";
import PatientTaskMetricsTable from "./PatientTaskMetricsTable";
import useApi from "../../shared/hooks/useApi";
import React from "react";
import { Geo } from "../../../../public/admin/scripts/messages/address_component";
import PatientTaskMetricsMap from "./PatientTaskMetricsMap";
import { createFilters } from "../../shared/hooks/useFilters";
import FreeTextSearch from "../../shared/components/FreeTextSearch";
import { MapRef } from "react-map-gl/maplibre";

interface Props {
  patientTaskInstanceId: PatientTaskInstanceId;
  onClose: () => void;
}
type FilterState = QueryParamsOf<"get", "./patient_task_instances/:patientTaskInstanceId/metrics">;

const initialFilterState: FilterState = {
  taskType: "received",
};

export default function PatientTaskMetricsModal(props: Props) {
  const { queries } = useApi();
  const [filters, setFilters] = React.useState<FilterState>(initialFilterState);

  const metricsQueryOptions = queries.patientTaskMetrics.metrics(
    filters,
    props.patientTaskInstanceId
  );
  const typeCountQueryOptions = queries.patientTaskMetrics.getCount(props.patientTaskInstanceId);

  const typeCountQuery = useQuery({
    ...typeCountQueryOptions,
    select: (data) => {
      const mappedTypeCountQuery = new Map<Messages["PatientTaskInstanceMetricType"], number>();
      data.typeCount.forEach((x) => {
        mappedTypeCountQuery.set(x.type, x.count);
      });
      return Object.fromEntries(mappedTypeCountQuery);
    },
  });

  const metricsQuery = useQuery({
    ...metricsQueryOptions,
  });

  const { createFreeTextFilter } = createFilters<FilterState>();
  const rnNameFilter = createFreeTextFilter({
    debounce: 300,
    name: "RNName",
    placeholder: "Enter RN Name",
    value: filters.RNName ?? null,
    onChange: (key, value) => setFilters((prev) => ({ ...prev, [key]: value })),
  });
  const zipCodeFilter = createFreeTextFilter({
    debounce: 300,
    name: "zipCode",
    placeholder: "Enter Zip Code",
    value: filters.zipCode ?? null,
    onChange: (key, value) => setFilters((prev) => ({ ...prev, [key]: value })),
  });

  const filterNode = (
    <Flex gap={4}>
      <FreeTextSearch {...rnNameFilter} />
      <FreeTextSearch {...zipCodeFilter} />
    </Flex>
  );
  const mapRef = React.useRef<MapRef>(null);

  const [selectedTaskType, setSelectedTaskType] =
    React.useState<Messages["PatientTaskInstanceMetricType"]>("received");

  const handleClose = () => {
    setFilters((prev) => ({ ...prev, taskType: selectedTaskType }));
    props.onClose();
  };

  const handleClickRow = (addressGeoLocation: Geo) => {
    mapRef.current?.flyTo({
      center: [addressGeoLocation.lng, addressGeoLocation.lat],
      zoom: 13,
      animate: true,
      pitch: 45,
      bearing: 0,
    });
  };

  const handleClickHome = (marker: Geo) => {
    mapRef.current?.flyTo({
      center: [marker.lng, marker.lat],
      zoom: 12,
      animate: true,
      pitch: 45,
      bearing: 0,
    });
  };

  const handleTaskTypeChange = (taskType: Messages["PatientTaskInstanceMetricType"]) => {
    setSelectedTaskType(taskType);
    setFilters((prev) => ({ ...prev, taskType }));
  };

  return (
    <Modal isCentered isOpen={true} scrollBehavior="inside" size="full" onClose={handleClose}>
      <ModalOverlay />
      <ModalContent height="100%">
        <ModalCloseButton size="lg" onClick={handleClose} />
        <ModalHeader display="flex" flexDirection="column" gap={4}>
          <Heading size="lg">Patient Task Metrics</Heading>
        </ModalHeader>
        <ModalBody display="flex" flexDirection="column" gap={4} height="100%">
          <Flex gap={4} justifyContent="space-between">
            <Text fontSize="lg">
              <b>Patient Name:</b> {metricsQuery.data?.patientName}
            </Text>
            <Text fontSize="lg">
              <b>Patient Address:</b> {metricsQuery.data?.patientAddress}
            </Text>
            <Text fontSize="lg">
              <b>Task Dates Range:</b> {metricsQuery.data?.taskDatesRange}
            </Text>
            <Text fontSize="lg">
              <b>Task Type:</b> {metricsQuery.data?.taskType}
            </Text>
            <Text fontSize="lg">
              <b>Cert Period Range:</b> {metricsQuery.data?.certPeriodRange}
            </Text>
          </Flex>
          <Tabs>
            <TabList>
              <Tab
                alignItems="center"
                display="flex"
                gap={2}
                onClick={() => handleTaskTypeChange("received")}
              >
                Broadcasted To{" "}
                <Badge
                  colorScheme={
                    typeCountQuery.data?.received !== undefined && typeCountQuery.data?.received > 0
                      ? "blue"
                      : "gray"
                  }
                  fontSize="sm"
                  p={1}
                >
                  {typeCountQuery.data?.received}
                </Badge>
              </Tab>
              <Tab
                alignItems="center"
                display="flex"
                gap={2}
                onClick={() => handleTaskTypeChange("viewed")}
              >
                Viewed{" "}
                <Badge
                  colorScheme={
                    typeCountQuery.data?.viewed !== undefined && typeCountQuery.data?.viewed > 0
                      ? "blue"
                      : "gray"
                  }
                  fontSize="sm"
                  p={1}
                >
                  {typeCountQuery.data?.viewed}
                </Badge>
              </Tab>
              <Tab
                alignItems="center"
                display="flex"
                gap={2}
                onClick={() => handleTaskTypeChange("opened")}
              >
                Opened{" "}
                <Badge
                  colorScheme={
                    typeCountQuery.data?.opened !== undefined && typeCountQuery.data?.opened > 0
                      ? "blue"
                      : "gray"
                  }
                  fontSize="sm"
                  p={1}
                >
                  {typeCountQuery.data?.opened}
                </Badge>
              </Tab>
              <Tab
                alignItems="center"
                display="flex"
                gap={2}
                onClick={() => handleTaskTypeChange("scheduled")}
              >
                Scheduled{" "}
                <Badge
                  colorScheme={
                    typeCountQuery.data?.scheduled !== undefined &&
                    typeCountQuery.data?.scheduled > 0
                      ? "blue"
                      : "gray"
                  }
                  fontSize="sm"
                  p={1}
                >
                  {typeCountQuery.data?.scheduled}
                </Badge>
              </Tab>
              <Tab
                alignItems="center"
                display="flex"
                gap={2}
                onClick={() => handleTaskTypeChange("unscheduled")}
              >
                Unscheduled{" "}
                <Badge
                  colorScheme={
                    typeCountQuery.data?.unscheduled !== undefined &&
                    typeCountQuery.data?.unscheduled > 0
                      ? "blue"
                      : "gray"
                  }
                  fontSize="sm"
                  p={1}
                >
                  {typeCountQuery.data?.unscheduled}
                </Badge>
              </Tab>
            </TabList>
            <TabPanels>
              <Flex gap={4} height="100%" width="100%">
                <PatientTaskMetricsTable
                  filterNode={filterNode}
                  patientTaskInstanceId={props.patientTaskInstanceId}
                  queryOptions={metricsQueryOptions}
                  onClickCaregiver={handleClose}
                  onClickRow={handleClickRow}
                />
                {metricsQuery.data && metricsQuery.data.patientAddressComponents && (
                  <PatientTaskMetricsMap
                    ref={mapRef}
                    caregiverMarkers={
                      metricsQuery.data.metrics.map((metric) => ({
                        addressGeoLocation: metric.caregiver.addressGeoLocation,
                        photoUrl: metric.caregiver.photoUrl,
                      })) ?? []
                    }
                    patientMarker={metricsQuery.data.patientAddressComponents.location}
                    onClickHome={handleClickHome}
                  />
                )}
              </Flex>
            </TabPanels>
          </Tabs>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
}
